import * as React from "react";
import {translate} from "react-i18next";
import {compose, Dispatch} from "redux";
import {connect} from "react-redux";
import {IReduxState} from "src/redux/IReduxState";

import ErrorBoundary from "../../utils/ErrorBoundary";
import {CnrDto, ModeltypeBasicDto, ModelyearDto, TechnicalDataDto, TypefamilyDto} from "../../services/Client";
import {GetTechnicalDataDerivatesParams, GetTechnicalDataParams} from "../../services/HATEOASClient";
import GeneralActions from "../../redux/actions/GeneralActions";
import TechnicalDataViewActions from "../../redux/actions/TechnicalDataViewActions";
import ViewContainer from "../ViewContainer";
import {TechnicalDataHeader} from "./TechnicalDataHeader";
import {TechnicalDataTable} from "./TechnicalDataTable";
import {ITranslatedComponent, ITypefamilyMap} from "../../redux/reducers/generalReducer";
import {
    selectLanguage,
    selectTechnicalData,
    selectTechnicalDataDerivates,
    selectUserCnrs,
    selectUserModelyearsElements,
    selectUserTypefamiliesMap
} from "../../redux/selectors";

interface IStateToProps {
    userCnrs: CnrDto[];
    userModelyears: ModelyearDto[];
    userTypefamiliesMap: ITypefamilyMap;
    technicalDataDerivates: ModeltypeBasicDto[];
    technicalData: TechnicalDataDto[];
    userLanguage: string;
}

interface IDispatchToProps {
    addTranslationLocation: () => void;
    requestTechnicalData: (t: reactI18Next.TranslationFunction, params: GetTechnicalDataParams) => void;
    requestTechnicalDataDerivates: (t: reactI18Next.TranslationFunction, params: GetTechnicalDataDerivatesParams) => void;
    unloadTechnicalData: () => void;
    unloadTechnicalDataDerivates: () => void;

}

interface IOwnProps {
}

interface ITechnicalDataViewProps extends IStateToProps, IDispatchToProps, IOwnProps, ITranslatedComponent {

}

interface ITechnicalDataViewState {
    selectedCnr?: CnrDto;
    selectedModelyear?: ModelyearDto;
    selectedTypefamily?: TypefamilyDto;
    selectedDerivate?: ModeltypeBasicDto;

    filteredTypefamilies: TypefamilyDto[];
}

class TechnicalDataView extends React.Component<ITechnicalDataViewProps, ITechnicalDataViewState> {

    constructor(props: ITechnicalDataViewProps) {
        super(props);

        this.state = {
            filteredTypefamilies: []
        };
    }

    componentDidMount(): void {
        this.props.addTranslationLocation();
    }

    componentWillUnmount() {
        this.props.unloadTechnicalData();
        this.props.unloadTechnicalDataDerivates();
    }

    componentDidUpdate(prevProps: ITechnicalDataViewProps, prevState: ITechnicalDataViewState): void {
        const {selectedCnr, selectedModelyear, selectedTypefamily, selectedDerivate} = this.state;
        const mainSelectionChanged = prevState.selectedCnr !== selectedCnr ||
            prevState.selectedModelyear !== selectedModelyear ||
            prevState.selectedTypefamily !== selectedTypefamily;

        if (mainSelectionChanged) {
            this.props.unloadTechnicalDataDerivates();

            if (selectedCnr && selectedModelyear && selectedTypefamily) {
                const param: GetTechnicalDataDerivatesParams = {
                    cnr: selectedCnr.cnr,
                    modelyearId: selectedModelyear.objectId,
                    typefamilyId: selectedTypefamily.objectId
                };
                this.props.requestTechnicalDataDerivates(this.props.t, param);
            }
        }

        if (mainSelectionChanged || prevState.selectedDerivate !== selectedDerivate) {
            if (selectedCnr && selectedModelyear && selectedTypefamily && selectedDerivate) {
                this.props.requestTechnicalData(
                    this.props.t,
                    {
                        modelyear: selectedModelyear.mj4,
                        cnr: selectedCnr.cnr,
                        // already restricted in the dropdown data source
                        btyp: selectedDerivate.btyp || ""
                    }
                );
            } else if (this.props.technicalData.length > 0) {
                this.props.unloadTechnicalData();
            }
        }
    }

    handleCnrChange = (selectedCnr?: CnrDto) => {
        this.setState({
            selectedCnr,
            selectedDerivate: undefined
        });
    }

    handleModelyearChange = (selectedModelyear?: ModelyearDto) => {
        const {userTypefamiliesMap} = this.props;
        const filteredTypefamilies = selectedModelyear && userTypefamiliesMap[selectedModelyear.mj1]
            ? userTypefamiliesMap[selectedModelyear.mj1].elements || [] : [];

        this.setState({
            selectedModelyear,
            selectedTypefamily: undefined,
            selectedDerivate: undefined,
            filteredTypefamilies
        });
    }

    handleTypefamilyChange = (selectedTypefamily?: TypefamilyDto) => {
        this.setState({
            selectedTypefamily,
            selectedDerivate: undefined
        });
    }

    handleDerivateChange = (selectedDerivate?: ModeltypeBasicDto) => {
        this.setState({selectedDerivate});
    }

    render() {
        const {
            selectedCnr,
            selectedModelyear,
            selectedTypefamily,
            selectedDerivate,
            filteredTypefamilies
        } = this.state;

        return (
            <ViewContainer >
                <ErrorBoundary >
                    <TechnicalDataHeader
                        t={this.props.t}
                        selectedCnr={selectedCnr}
                        userCnrs={this.props.userCnrs}
                        handleCnrChange={this.handleCnrChange}
                        selectedModelyear={selectedModelyear}
                        userModelyears={this.props.userModelyears}
                        handleModelyearChange={this.handleModelyearChange}
                        selectedTypefamily={selectedTypefamily}
                        userTypefamilies={filteredTypefamilies}
                        handleTypefamilyChange={this.handleTypefamilyChange}
                        selectedDerivate={selectedDerivate}
                        derivates={this.props.technicalDataDerivates}
                        handleDerivateChange={this.handleDerivateChange}
                        userLanguage={this.props.userLanguage}
                    />
                </ErrorBoundary>
                <ErrorBoundary>
                    <TechnicalDataTable
                        t={this.props.t}
                        technicalData={this.props.technicalData}
                        contextSelected={selectedCnr !== undefined && selectedModelyear !== undefined
                            && selectedTypefamily !== undefined && selectedDerivate !== undefined}
                    />
                </ErrorBoundary>
            </ViewContainer>
        );
    }
}

const mapStateToProps = (state: IReduxState): IStateToProps => ({
    userCnrs: selectUserCnrs(state),
    userModelyears: selectUserModelyearsElements(state) || [],
    userTypefamiliesMap: selectUserTypefamiliesMap(state),
    technicalDataDerivates: selectTechnicalDataDerivates(state),
    technicalData: selectTechnicalData(state) || [],
    userLanguage: selectLanguage(state)
});

const mapDispatchToProps = (dispatch: Dispatch<IReduxState>): IDispatchToProps => {
    return {
        addTranslationLocation: () => dispatch(GeneralActions.AddTranslationLocation({
            pattern: "technicaldata",
            substitutes: "TECHNICAL_DATA"
        })),
        requestTechnicalData: (t: reactI18Next.TranslationFunction, params: GetTechnicalDataParams) =>
            dispatch(TechnicalDataViewActions.GetTechnicalData(t, params)),
        requestTechnicalDataDerivates: (t: reactI18Next.TranslationFunction, params: GetTechnicalDataDerivatesParams) =>
            dispatch(TechnicalDataViewActions.GetTechnicalDataDerivates(t, params)),
        unloadTechnicalData: () => dispatch(TechnicalDataViewActions.UnloadTechnicalData()),
        unloadTechnicalDataDerivates: () => dispatch(TechnicalDataViewActions.UnloadTechnicalDataDerivates())
    };
};

export default compose<React.ComponentType<IOwnProps>>(
    translate("TECH_DATA_VIEW"),
    connect<IStateToProps, IDispatchToProps>(mapStateToProps, mapDispatchToProps)
)(TechnicalDataView);



// WEBPACK FOOTER //
// ./src/views/TechnicalDataView/TechnicalDataView.tsx